/**
 * One write primitive for the console (rd round 12, lane A5a, item A5-1).
 *
 * WHY THIS EXISTS (rc-2 RC-A / RC-B). `runOnce` lives in a page file and
 * disables a hand-picked list of buttons; `queue.ts` carries five `setBusy`
 * copies; an Enter handler or a shortcut calls the action directly and skips
 * every one of them. Meanwhile a refusal, a lost response and a write that
 * landed whose re-read failed all read the same sentence, because nothing in
 * the portal names the difference.
 *
 * WHAT THIS DOES. `runWrite` is the one place a write is run:
 *   - one flight per `key` (a second press shares the first run, so a held key
 *     or a double click sends once);
 *   - the whole `scope` container is disabled while it runs, not a button list,
 *     so a sibling control cannot send a second request;
 *   - `gate()` is the one function every submit path calls (click, Enter,
 *     shortcut, programmatic); it returns the reason to show, or null;
 *   - the result is an outcome, not a boolean: `landed`, `refused(status)`,
 *     `conflict`, `unknown`. Only the caller turns that into words, through
 *     `outcomeSentence` in `./failure.ts` (one home, so this module does not
 *     import it and there is no cycle);
 *   - an `unknown` outcome is verified with `readBack()` when the page has one;
 *     a `conflict` (409) may be too, because a 409 on a retry can mean the first
 *     attempt landed, and a read-back that finds the change clears the
 *     possibly-sent mark;
 *   - an error inside `afterLanded` (a reload, a re-paint) is swallowed and
 *     reported as `afterLandedFailed`, so a landed write can never be reported
 *     as "did not save" and the page can still say the repaint failed;
 *   - `stillCurrent()` gates the announcement of a gate's reason and the
 *     re-paint after a landed write: a page that has moved on is not touched;
 *   - a request that never got an answer is remembered by key
 *     (`wasPossiblySent`) even across a repaint, so a page can offer to adopt
 *     what already exists instead of sending it twice; `blockWhenPossiblySent`
 *     opts a caller into refusing the retry outright, and that outcome is
 *     `{ kind: "unknown", blocked: true }` so the page can word it. A page that
 *     adopts or verifies the request must call `forgetPossiblySent(key)` or the
 *     key stays blocked forever;
 *   - a proxy timeout (408, 425, 499) is `unknown`, not a refusal: the server
 *     may have acted.
 *
 * WHAT THIS DOES NOT DO. It does not migrate a page (wave B does), it does not
 * choose words, and it does not replace `runAnnouncedAction` (that one is now a
 * thin wrapper over `failureSentence`; see `./announced-action.ts`).
 */
import { AdminApiError } from "../api.js";
import { ConsoleDataError } from "../console/data-source.js";

export type WriteOutcome =
  /** The write landed. `afterLandedFailed` says the re-paint failed, not the write. */
  | { kind: "landed"; afterLandedFailed?: boolean }
  /**
   * The error `send` threw (B0-P3), so a page can word the outcome with
   * `failureCopy` — "already resolved" apart from another 409, decision 1's 403
   * sentence, and a scrubbed "Show details". Absent on a refusal with
   * `status: 0` (the gate refused: nothing was sent) and on a blocked retry.
   */
  | { kind: "refused"; status: number; error?: unknown }
  | { kind: "conflict"; error?: unknown }
  /**
   * The answer is not known: a lost response, a 5xx, a proxy timeout, or a
   * retry the primitive refused because the first try may have landed.
   * `blocked` is set for the last of these, so a page can say the request may
   * already have gone through instead of inviting another press.
   */
  | { kind: "unknown"; blocked?: boolean; error?: unknown };

export interface RunWriteOptions<T> {
  /** One flight per key. Use something stable per logical action (row + action). */
  key: string;
  /** The container whose controls are disabled while the write runs. */
  scope: HTMLElement;
  /** The one gate every submit path calls: the reason to show, or null. */
  gate: () => string | null;
  send: () => Promise<T>;
  /** Re-read the server's truth after an unknown outcome (or a 409). */
  readBack?: () => Promise<boolean>;
  /** Runs after the write landed. Its failure never demotes the outcome. */
  afterLanded?: (value: T) => void | Promise<void>;
  /** False once the page has moved on: nothing is announced or repainted after that. */
  stillCurrent: () => boolean;
  /**
   * Refuse to send when this key is remembered as possibly sent (a lost
   * response the page never verified). The outcome is `{ kind: "unknown",
   * blocked: true }`: the request may already have been applied, so a second
   * send could apply it twice (the credential re-issue case). Off by default,
   * so a page that can verify with a `readBack` keeps the retry.
   *
   * A page that turns `blocked` into words must give the person a way out: read
   * the server's truth, and when it confirms what the first try was trying to
   * do, call `forgetPossiblySent(key)` (or adopt the row) so the key is not
   * blocked forever. Word it like "A request like this may already have gone
   * through. Check before trying again." — never "refresh, then try again",
   * which is the loop this flag exists to break.
   */
  blockWhenPossiblySent?: boolean;
  /** Where a gate's own reason is announced (the gate supplies the sentence). */
  live?: HTMLElement;
}

const inFlight = new Map<string, Promise<WriteOutcome>>();
const possiblySent = new Set<string>();

/**
 * The fingerprint of a request that may have reached GV Books without an
 * answer. A page reads it after a repaint to offer "adopt what exists" instead
 * of sending again.
 */
export function wasPossiblySent(fingerprint: string): boolean {
  return possiblySent.has(fingerprint);
}

/** Record that a request may have been applied (a lost response). */
export function rememberPossiblySent(fingerprint: string): void {
  possiblySent.add(fingerprint);
}

/** A confirmed outcome is no longer merely possible. */
export function forgetPossiblySent(fingerprint: string): void {
  possiblySent.delete(fingerprint);
}

/** Test seam: no possibly-sent memory survives between tests. */
export function forgetPossiblySentForTests(): void {
  possiblySent.clear();
}

/**
 * Classify an error thrown by `send`.
 *
 * A 409 is a conflict (decide once; a page with a `readBack` can verify it). A
 * 5xx or a lost response is `unknown`: the server may have acted, and the only
 * honest answer is to re-read. A 4xx that the server refused changed nothing:
 * `refused`. An unrecognised error is `unknown` — the safe direction, because it
 * triggers the read-back rather than claiming nothing saved.
 */
export function classifyWriteError(error: unknown): WriteOutcome {
  if (error instanceof AdminApiError || error instanceof ConsoleDataError) {
    const status = error.status;
    if (status === 409) return { kind: "conflict" };
    // A proxy timeout is not a refusal: the request may have reached the server.
    if (status === 408 || status === 425 || status === 499) return { kind: "unknown" };
    if (status === 401 || status === 429) return { kind: "refused", status };
    if (status >= 400 && status < 500) return { kind: "refused", status };
    return { kind: "unknown" };
  }
  return { kind: "unknown" };
}

/**
 * Run one write. See the module docstring for the guarantees; `WriteOutcome` is
 * the only answer, and the caller turns it into words.
 */
export function runWrite<T>(opts: RunWriteOptions<T>): Promise<WriteOutcome> {
  const running = inFlight.get(opts.key);
  if (running !== undefined) return running;
  const run = execute(opts).finally(() => {
    inFlight.delete(opts.key);
  });
  inFlight.set(opts.key, run);
  return run;
}

async function execute<T>(opts: RunWriteOptions<T>): Promise<WriteOutcome> {
  const reason = opts.gate();
  if (reason !== null) {
    if (opts.live !== undefined && opts.stillCurrent()) opts.live.textContent = reason;
    return { kind: "refused", status: 0 };
  }
  // A retry of a request that may already have landed must not send again.
  // `blocked` tells the page why nothing was sent, so it can offer the adopt or
  // forget path instead of an invitation to press again.
  if (opts.blockWhenPossiblySent === true && possiblySent.has(opts.key)) {
    return { kind: "unknown", blocked: true };
  }

  let release: () => void = () => undefined;
  try {
    release = disableScope(opts.scope);
    let value: T;
    try {
      value = await opts.send();
    } catch (error) {
      let outcome = classifyWriteError(error);
      if (outcome.kind === "unknown" || outcome.kind === "conflict") {
        outcome = await verify(opts.readBack, outcome);
      }
      // A read-back that found the change is a confirmation: the request is no
      // longer merely possibly sent, even if an earlier try was.
      if (outcome.kind === "landed") {
        forgetPossiblySent(opts.key);
        return outcome;
      }
      if (outcome.kind === "unknown") rememberPossiblySent(opts.key);
      // B0-P3: every non-landed outcome carries the error `send` threw — the
      // request's own error, never the read-back's — so the page can say
      // whether it was an "already resolved" 409, a decision 1 refusal, or a
      // lost response. Nothing is attached when nothing was sent.
      return { ...outcome, error };
    }
    forgetPossiblySent(opts.key);
    let afterLandedFailed = false;
    if (opts.afterLanded !== undefined && opts.stillCurrent()) {
      try {
        await opts.afterLanded(value);
      } catch {
        // A failed reload/re-paint is not a failed write. The outcome stays
        // landed; the page's own read error is its to show, and the flag tells
        // it so without inviting a second send.
        afterLandedFailed = true;
      }
    }
    return afterLandedFailed ? { kind: "landed", afterLandedFailed: true } : { kind: "landed" };
  } finally {
    release();
  }
}

/** Read the server's truth after an unknown outcome (or a 409). */
async function verify(
  readBack: (() => Promise<boolean>) | undefined,
  outcome: WriteOutcome,
): Promise<WriteOutcome> {
  if (readBack === undefined) return outcome;
  try {
    return (await readBack()) ? { kind: "landed" } : outcome;
  } catch {
    return { kind: "unknown" };
  }
}

/**
 * Disable every control in the scope, and return the restore. Only the controls
 * this disabled are re-enabled: a control that was already disabled (because,
 * say, the row says the action is not available) stays disabled.
 */
function disableScope(scope: HTMLElement): () => void {
  const controls = Array.from(
    scope.querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "button, input, select, textarea",
    ),
  );
  const enabled = controls.filter((control) => !control.disabled);
  for (const control of enabled) control.disabled = true;
  const hadBusy = scope.hasAttribute("aria-busy");
  scope.setAttribute("aria-busy", "true");
  return () => {
    for (const control of enabled) control.disabled = false;
    if (!hadBusy) scope.removeAttribute("aria-busy");
  };
}
